import React from "react";
import { Search, List, LayoutGrid } from "lucide-react";

export interface AppOption {
  id: string;
  name: string;
}

export interface IssueFilterBarProps {
  searchQuery: string;
  onSearchChange: (q: string) => void;
  selectedApp: string;
  onAppChange: (app: string) => void;
  selectedPriority: string;
  onPriorityChange: (priority: string) => void;
  selectedStatus: string;
  onStatusChange: (status: string) => void;
  selectedAssignment?: string;
  onAssignmentChange?: (assignment: string) => void;
  selectedCategory?: string;
  onCategoryChange?: (category: string) => void;
  availableApps?: AppOption[];
  viewMode: "list" | "kanban";
  onViewModeChange: (mode: "list" | "kanban") => void;
}

export const IssueFilterBar: React.FC<IssueFilterBarProps> = ({
  searchQuery,
  onSearchChange,
  selectedApp,
  onAppChange,
  selectedPriority,
  onPriorityChange,
  selectedStatus,
  onStatusChange,
  selectedAssignment = "all",
  onAssignmentChange,
  selectedCategory = "all",
  onCategoryChange,
  availableApps = [],
  viewMode,
  onViewModeChange,
}) => {
  // Ensure "all" is never duplicated and apps are cleanly listed
  const appOptions = availableApps.filter((a) => a.id !== "all");

  return (
    <div className="glass rounded-xl p-4 border border-slate-200 shadow-xs flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
      {/* Search Input */}
      <div className="relative flex-1 min-w-[200px]">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by ticket ID, title, description, or reporter..."
          className="w-full pl-9 pr-3 py-2 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 shadow-xs"
        />
      </div>

      {/* Filter Dropdowns */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Dynamic App Filter */}
        <select
          aria-label="Filter by Application"
          value={selectedApp}
          onChange={(e) => onAppChange(e.target.value)}
          className="px-2.5 py-2 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 shadow-xs font-medium"
        >
          <option value="all">All Applications</option>
          {appOptions.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>

        {/* Category Filter: Bug vs Suggestion */}
        {onCategoryChange && (
          <select
            aria-label="Filter Category"
            value={selectedCategory}
            onChange={(e) => onCategoryChange(e.target.value)}
            className="px-2.5 py-2 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 shadow-xs font-medium"
          >
            <option value="all">All Categories</option>
            <option value="bug">Bugs</option>
            <option value="feature_request">Suggestions / Feedback</option>
            <option value="improvement">Improvements</option>
          </select>
        )}

        {/* Priority Filter */}
        <select
          aria-label="Filter Priority"
          value={selectedPriority}
          onChange={(e) => onPriorityChange(e.target.value)}
          className="px-2.5 py-2 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 shadow-xs"
        >
          <option value="all">All Priorities</option>
          <option value="critical">Critical (1h SLA)</option>
          <option value="high">High (4h SLA)</option>
          <option value="medium">Medium (24h SLA)</option>
          <option value="low">Low (48h SLA)</option>
        </select>

        {/* Status Filter */}
        <select
          aria-label="Filter Status"
          value={selectedStatus}
          onChange={(e) => onStatusChange(e.target.value)}
          className="px-2.5 py-2 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 shadow-xs font-medium"
        >
          <option value="active">Active Tickets</option>
          <option value="all">All Statuses</option>
          <option value="new">New / Unassigned</option>
          <option value="in_review">In Review</option>
          <option value="in_progress">In Progress</option>
          <option value="pending_user_feedback">Pending Feedback</option>
          <option value="resolved">Resolved Only</option>
          <option value="closed">Closed Only</option>
        </select>

        {/* Assignment Filter */}
        {onAssignmentChange && (
          <select
            aria-label="Filter Assignment"
            value={selectedAssignment}
            onChange={(e) => onAssignmentChange(e.target.value)}
            className={`px-2.5 py-2 text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 shadow-xs font-medium transition-colors ${
              selectedAssignment === "unassigned"
                ? "bg-amber-50 text-amber-900 border-amber-300 ring-1 ring-amber-300"
                : "bg-white text-slate-800 border-slate-200"
            }`}
          >
            <option value="all">All Assignments</option>
            <option value="unassigned">Unassigned Only</option>
            <option value="assigned">Assigned Only</option>
          </select>
        )}

        {/* View Mode Switcher */}
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-xs">
          <button
            type="button"
            onClick={() => onViewModeChange("list")}
            aria-label="List View"
            className={`p-1.5 rounded-md transition-colors ${
              viewMode === "list"
                ? "bg-teal-600 text-white shadow-xs"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <List className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => onViewModeChange("kanban")}
            aria-label="Kanban Board View"
            className={`p-1.5 rounded-md transition-colors ${
              viewMode === "kanban"
                ? "bg-teal-600 text-white shadow-xs"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
